/**
 * OnlineMatchPanel — the per-game "Play online" surface on the game page. Two
 * ways two signed-in players deliberately meet for a match:
 *   • Host a match → mints an invite, seats you as player 1, drops you into the
 *     room to wait; other players discover it from this game's panel.
 *   • Join an open challenge → you become player 2 and land in the host's room.
 *
 * A bilateral-ready game (`hostEntersAfterAccept`: a Composition V2 version,
 * whose server admits only an ACCEPTED invitation and starts once both
 * players are ready, unranked) keeps the host here instead: the panel watches
 * the hosted invite and enters the room the moment an opponent accepts.
 *
 * Gated by the caller on online-eligibility; `ready` additionally reflects that
 * the active version's baked shell can read the pairing token (a not-yet-
 * repackaged game shows a note instead of broken pairing).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MatchInvite } from '@djgg/creations';
import { useAuth, useServices } from '../services/context.js';
import { useSubscription } from '../hooks/useSubscription.js';
import { useOnlineMatch } from '../hooks/useOnlineMatch.js';

export function OnlineMatchPanel({
  creationId,
  ready,
  hostEntersAfterAccept,
}: {
  creationId: string;
  /** Online-eligible AND the active version's shell honors `?match=` (>=0.8.0). */
  ready: boolean;
  /** Bilateral-ready game: the host enters only once an opponent accepted. */
  hostEntersAfterAccept: boolean;
}) {
  const { user } = useAuth();
  const services = useServices();
  const { host, hosted, releaseHosted, enter, join, cancel, busy, error } = useOnlineMatch();
  const invites = useSubscription<Array<MatchInvite & { id: string }>>(
    (cb) => services.subscribe.openMatchInvites(creationId, cb),
    [],
    [creationId],
  );

  const now = Date.now();
  const open = invites.filter((i) => i.status === 'open' && i.expiresAt > now);
  const mine = open.find((i) => i.hostUid === user?.uid);
  const others = open.filter((i) => i.hostUid !== user?.uid);

  // The invite a bilateral-ready host waits on: the one this page just hosted,
  // or (after a reload) its own open challenge. Kept once known, because an
  // accepted invite leaves the open list in the same instant it becomes enterable.
  const [awaitedToken, setAwaitedToken] = useState<string | null>(null);
  const candidateToken = hostEntersAfterAccept ? (hosted?.token ?? mine?.token ?? null) : null;
  useEffect(() => {
    if (candidateToken && candidateToken !== awaitedToken) setAwaitedToken(candidateToken);
  }, [candidateToken, awaitedToken]);
  const awaited = useSubscription<(MatchInvite & { id: string }) | null>(
    (cb) => (awaitedToken ? services.subscribe.matchInvite(awaitedToken, cb) : () => {}),
    null,
    [awaitedToken],
  );
  const [entering, setEntering] = useState(false);
  useEffect(() => {
    if (!awaited || entering || awaited.hostUid !== user?.uid) return;
    if (awaited.status === 'accepted' && awaited.guestUid) {
      setEntering(true);
      enter({ creationId: awaited.creationId, version: awaited.version, token: awaited.token });
    } else if (awaited.status !== 'open') {
      // Cancelled or expired: offer hosting again.
      if (hosted?.token === awaited.token) releaseHosted();
      setAwaitedToken(null);
    }
  }, [awaited, entering, enter, hosted, releaseHosted, user?.uid]);
  const waiting = hostEntersAfterAccept && !!awaitedToken && awaited?.status === 'open';

  return (
    <div className="panel" data-testid="online-match">
      <h2 className="panel__title">{hostEntersAfterAccept ? '⚔ Play online' : '⚔ Play online — ranked'}</h2>

      {!user ? (
        <p className="muted">
          <Link to="/login">Sign in</Link> to host or join a ranked online match.
        </p>
      ) : !ready ? (
        <p className="muted" data-testid="online-not-ready">
          Online challenges need a quick repackage of this game — check back soon.
        </p>
      ) : (
        <div className="stack" style={{ gap: 12 }}>
          {entering ? (
            <div className="row" data-testid="entering-match">
              <span>✅ Opponent joined — entering the match…</span>
            </div>
          ) : waiting && awaited ? (
            <div className="row" style={{ gap: 8, alignItems: 'center' }} data-testid="awaiting-opponent">
              <span>⏳ Your challenge is open — keep this page open; you’ll enter the match as soon as an opponent joins.</span>
              <button
                className="btn btn--ghost btn--sm"
                onClick={() => void cancel(awaited.token)}
                disabled={busy === `cancel:${awaited.token}`}
              >
                Cancel
              </button>
            </div>
          ) : mine && !hostEntersAfterAccept ? (
            <div className="row" style={{ gap: 8, alignItems: 'center' }} data-testid="my-open-invite">
              <span>⏳ Your challenge is open — waiting for an opponent…</span>
              <button
                className="btn btn--ghost btn--sm"
                onClick={() => void cancel(mine.token)}
                disabled={busy === `cancel:${mine.token}`}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="row" style={{ gap: 10, alignItems: 'center' }}>
              <button
                className="btn btn--primary"
                data-testid="host-match"
                onClick={() => void host(creationId, { enterAfterAccept: hostEntersAfterAccept })}
                disabled={!!busy || (hostEntersAfterAccept && !!awaitedToken)}
              >
                {busy === `host:${creationId}` ? 'Starting…' : 'Host a match'}
              </button>
              <span className="muted" style={{ fontSize: 12 }}>
                {hostEntersAfterAccept
                  ? 'You’ll enter the match together as soon as someone joins.'
                  : 'You’ll wait in the room as player 1 until someone joins.'}
              </span>
            </div>
          )}

          <div>
            <div className="muted" style={{ fontSize: 13, marginBottom: 4 }}>
              Open challenges {others.length > 0 ? `(${others.length})` : ''}
            </div>
            {others.length === 0 ? (
              <p className="muted" data-testid="no-open-challenges" style={{ fontSize: 13 }}>
                No one’s waiting right now — host a match and invite a friend, or
                return here to resume it.
              </p>
            ) : (
              <ul className="stack" style={{ gap: 6, listStyle: 'none', padding: 0 }}>
                {others.map((inv) => (
                  <li key={inv.token} className="row" style={{ gap: 8, alignItems: 'center' }} data-testid="open-challenge">
                    <span>
                      <strong>@{inv.hostHandle}</strong> is waiting
                    </span>
                    <button
                      className="btn btn--sm btn--primary"
                      data-testid="join-match"
                      onClick={() => void join(inv.token)}
                      disabled={!!busy}
                    >
                      {busy === `join:${inv.token}` ? 'Joining…' : 'Join'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {error && <div className="error-text" data-testid="online-error">{error}</div>}
    </div>
  );
}
